/*  Adapted from mzabriskie's react-flipcard
 *  https://github.com/mzabriskie/react-flipcard
 */

import cx from 'classnames2';
import { has } from 'lodash';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';

import styles from './ReactFlipCard.scss';

export class ReactFlipCard extends Component<any, any> {
  static propTypes = {
    type: PropTypes.string,
    flipped: PropTypes.bool,
    disabled: PropTypes.bool,
    onFlip: PropTypes.func,
    onKeyDown: PropTypes.func,
    children(props, propName, componentName) {
      const prop = props[propName];

      if (React.Children.count(prop) !== 2) {
        return new Error(
          `\`${componentName}\` ` +
            `should contain exactly two children. ` +
            `The first child represents the front of the card. ` +
            `The second child represents the back of the card.`
        );
      }
    }
  };

  static defaultProps = {
    type: 'horizontal',
    flipped: false,
    disabled: false
  };

  focusElement: HTMLElement;
  front: HTMLElement;
  back: HTMLElement;
  focusBack: boolean;
  notifyFlip: boolean;

  displayName: 'ReactFlipCard';

  constructor(props) {
    super(props);

    this.state = {
      hasFocus: false,
      isFlipped: this.props.flipped
    };
  }

  componentDidMount() {
    this.hideFlippedSide();
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    // Make sure both sides are displayed for animation
    this.showBothSides();

    // Wait for display above to take effect
    setTimeout(() => {
      this.setState({
        isFlipped: newProps.flipped
      });
    }, 0);
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    // If card is flipping to back via props, track element for focus
    if (!this.props.flipped && nextProps.flipped) {
      // The element that focus will return to when flipped back to front
      this.focusElement = document.activeElement as HTMLElement;
      // Indicates that the back of card needs focus
      this.focusBack = true;
    }

    // If isFlipped has changed need to notify
    if (this.state.isFlipped !== nextState.isFlipped) {
      this.notifyFlip = true;
    }
  }

  componentDidUpdate() {
    // If card has flipped to front, and focus is still within the card
    // return focus to the element that triggered flipping to the back.
    if (!this.props.flipped && this.focusElement && this.contains(findDOMNode(this), document.activeElement)) {
      this.focusElement.focus();
      this.focusElement = null;
    } else if (this.focusBack) {
      // Direct focus to the back if needed
      this.back.focus();
      this.focusBack = false;
    }

    // Notify card being flipped
    if (this.notifyFlip && typeof this.props.onFlip === 'function') {
      this.props.onFlip(this.state.isFlipped);
      this.notifyFlip = false;
    }

    // Hide whichever side of the card is down
    setTimeout(this.hideFlippedSide, 600);
  }

  handleFocus = () => {
    if (this.props.disabled) return;

    this.setState({
      isFlipped: true
    });
  };

  handleBlur = () => {
    if (this.props.disabled) return;

    this.setState({
      isFlipped: false
    });
  };

  handleKeyDown = e => {
    if (typeof this.props.onKeyDown === 'function') {
      this.props.onKeyDown(e);
    }
  };

  render() {
    return (
      <div
        className={cx(
          styles.ReactFlipCard,
          this.props.type === 'vertical' ? styles.ReactFlipCardVertical : null,
          this.props.type !== 'vertical' ? styles.ReactFlipCardHorizontal : null,
          this.state.isFlipped ? styles.ReactFlipCardFlipped : null,
          !this.props.disabled ? styles.ReactFlipCardEnabled : null
        )}
        tabIndex={0}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
      >
        <div className={styles.Flipper}>
          <div
            className={styles.Front}
            ref={front => {
              this.front = front;
            }}
            tabIndex={-1}
            aria-hidden={this.state.isFlipped}
          >
            {this.props.children[0]}
          </div>
          <div
            className={styles.Back}
            ref={back => {
              this.back = back;
            }}
            tabIndex={-1}
            aria-hidden={!this.state.isFlipped}
          >
            {this.props.children[1]}
          </div>
        </div>
      </div>
    );
  }

  showBothSides = () => {
    this.front.style.display = '';
    this.back.style.display = '';
  };

  hideFlippedSide = () => {
    // This prevents the flipped side from being tabbable
    if (this.props.disabled) {
      if (this.state.isFlipped && this.front) {
        this.front.style.display = '';
      } else if (this.back) {
        this.back.style.display = '';
      }
    }
  };

  // Checks to see if a parent element contains a child element
  contains = (parent, child) => {
    let node = has(child, 'parentNode') ? child.parentNode : null;
    while (node !== null) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };
}
